import * as React from "react";
import { OpenChatWebSocketHandler } from "@/views/open-chat/components/webSocketHandler";
import { SidebarPromptImprovementHandler } from "@/views/open-chat/components/sidebar/sidebarPromptImprovementHandler";
import { OpenChatMessageRowAi } from "@/views/open-chat/components/message/messageRowAi";
import { OpenChatMessageRow } from "@/views/open-chat/components/message/messageRow";
import { Loader } from "@/components/ui/loader";
import { MessageInput } from "@/views/open-chat/components/messageInput";
import { useGetMessagesQuery, useSendMessageMutation } from "@/api/open-chat";
import { ulid } from "ulid";
import { OpenChatMessage } from "@/types/open-chat";
import * as analytics from "@/services/analytics";

interface Props {
  conversationId: string;
}

const emptyList: OpenChatMessage[] = [];
const initialMessage: OpenChatMessage = {
  id: "initial",
  content: "I'll help you write the perfect prompt",
  type: "ai",
};

export const SidebarPromptImprovements: React.FC<Props> = ({ conversationId }) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { data: messages = emptyList, isLoading: isLoadingFirst } = useGetMessagesQuery(conversationId);
  const [sendMessageMutation, { isLoading }] = useSendMessageMutation();

  const handleSendMessage = React.useCallback(
    async (message: string) => {
      analytics.promptEnter(message);
      await sendMessageMutation({
        conversationId,
        message,
        messageId: ulid(),
        prompt: "open-chat-prompt-generator",
      }).unwrap();

      if (contentRef.current !== null) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    },
    [conversationId, sendMessageMutation],
  );

  return (
    <>
      <OpenChatWebSocketHandler contentRef={contentRef} conversationId={conversationId} />
      <SidebarPromptImprovementHandler handleSendMessage={handleSendMessage} />
      <div className="flex flex-grow flex-col overflow-y-scroll bg-white p-4 opacity-80" ref={contentRef}>
        <OpenChatMessageRowAi message={initialMessage} allowCopy={false} isImprovement={false} />
        {messages.map(message => (
          <OpenChatMessageRow key={message.id} allowCopy={false} message={message} isImprovement={true} />
        ))}
        {isLoading && <Loader />}
      </div>
      <div className="flex flex-col bg-white p-4">
        <div className="mt-4">
          <MessageInput onSendMessage={handleSendMessage} isStreaming={false} isReady={!isLoading && !isLoadingFirst} />
        </div>
      </div>
    </>
  );
};
